import React, { Suspense } from 'react';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import ScrollToTop from './components/shared/ScrollToTop';
import ImpersonationBanner from './components/shared/ImpersonationBanner';
import NewsletterCapture from './components/marketing/NewsletterCapture';
import AppErrorBoundary from './components/shared/AppErrorBoundary';
import './App.css';

// Critical pages - load immediately
import { HomePage } from './pages/public';

// Lazy load non-critical pages for better initial bundle size
const AuthPage = lazyWithRetry(() => import('./pages/AuthPage'));
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));
const ClientDashboard = lazyWithRetry(() => import('./pages/ClientDashboard'));
const OwnerPortal = lazyWithRetry(() => import('./pages/OwnerPortal'));
const RepPortal = lazyWithRetry(() => import('./pages/RepPortal'));
const AdminDashboard = lazyWithRetry(() => import('./pages/AdminDashboard'));
const TexasNoticeManager = lazyWithRetry(() => import('./pages/TexasNoticeManager'));
const TexasClientManager = lazyWithRetry(() => import('./pages/TexasClientManager'));
const CampaignManager = lazyWithRetry(() => import('./pages/CampaignManager'));
const BulkOrderUtility = lazyWithRetry(() => import('./pages/BulkOrderUtility'));
const PostageDepositReturn = lazyWithRetry(() => import('./pages/PostageDepositReturn'));
const PerNoticeInvoiceReturn = lazyWithRetry(() => import('./pages/PerNoticeInvoiceReturn'));
const InvoicePaymentReturn = lazyWithRetry(() => import('./pages/InvoicePaymentReturn'));
const NotaryClientUploadPage = lazyWithRetry(() => import('./pages/NotaryClientUploadPage'));
const PayApplicationPage = lazyWithRetry(() => import('./pages/public/PayApplicationPage'));
const W8benPage = lazyWithRetry(() => import('./pages/W8benPage'));
const AgreementSignPage = lazyWithRetry(() => import('./pages/AgreementSignPage'));
const ProposalAcceptPage = lazyWithRetry(() => import('./pages/public/ProposalAcceptPage'));
const SetPasswordPage = lazyWithRetry(() => import('./pages/SetPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const ProcoreEmbedPage = lazyWithRetry(() => import('./pages/ProcoreEmbedPage'));
const AutoLoginPage = lazyWithRetry(() => import('./pages/AutoLoginPage'));
const SmsOptInPage = lazyWithRetry(() => import('./pages/public/SmsOptInPage'));
const FieldTestPage = lazyWithRetry(() => import('./pages/FieldTestPage'));
const ProfileSettingsPage = lazyWithRetry(() => import('./pages/ProfileSettingsPage'));
// UploadRedirect is imported eagerly (it's tiny — 20 lines) so legacy
// email links resolve instantly without waiting for a lazy chunk.
import UploadRedirect from './pages/UploadRedirect';

// Lazy load public pages
const ServicesPage = lazyWithRetry(() => import('./pages/public/ServicesPage'));
const PricingPage = lazyWithRetry(() => import('./pages/public/PricingPage'));
const FAQPage = lazyWithRetry(() => import('./pages/public/FAQPage'));
const AboutPage = lazyWithRetry(() => import('./pages/public/AboutPage'));
const CareersPage = lazyWithRetry(() => import('./pages/public/CareersPage'));
const ContactPage = lazyWithRetry(() => import('./pages/public/ContactPage'));
const LearningCenterPage = lazyWithRetry(() => import('./pages/public/LearningCenterPage'));
const BlogPage = lazyWithRetry(() => import('./pages/public/BlogPage'));
const TermsPage = lazyWithRetry(() => import('./pages/public/TermsPage'));
const PreliminaryNoticesPage = lazyWithRetry(() => import('./pages/public/PreliminaryNoticesPage'));
const MechanicsLiensPage = lazyWithRetry(() => import('./pages/public/MechanicsLiensPage'));
const BondClaimsPage = lazyWithRetry(() => import('./pages/public/BondClaimsPage'));
const NoticeOfIntentPage = lazyWithRetry(() => import('./pages/public/NoticeOfIntentPage'));
const ResearchServicesPage = lazyWithRetry(() => import('./pages/public/ResearchServicesPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('./pages/public/PrivacyPolicyPage'));
const TermsOfServicePage = lazyWithRetry(() => import('./pages/public/TermsOfServicePage'));
const TestimonialsPage = lazyWithRetry(() => import('./pages/public/TestimonialsPage'));
const NoticeDeadlineCalculatorPage = lazyWithRetry(() => import('./pages/public/NoticeDeadlineCalculatorPage'));
const DevelopersPage = lazyWithRetry(() => import('./pages/public/DevelopersPage'));
const DesignProfessionalsPage = lazyWithRetry(() => import('./pages/public/DesignProfessionalsPage'));
const ZapierIntegrationPage = lazyWithRetry(() => import('./pages/ZapierIntegrationPage'));

// Lazy load state-specific landing pages (SEO - loaded on demand)
const FloridaPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/FloridaPreliminaryNotice'));
const FloridaNoticeOfCommencement = lazyWithRetry(() => import('./pages/landing/FloridaNoticeOfCommencement'));
const TexasPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/TexasPreliminaryNotice'));
const CaliforniaPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/CaliforniaPreliminaryNotice'));
const ArizonaPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/ArizonaPreliminaryNotice'));
const GeorgiaPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/GeorgiaPreliminaryNotice'));
const NevadaPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/NevadaPreliminaryNotice'));
const LouisianaPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/LouisianaPreliminaryNotice'));
const NewYorkPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/NewYorkPreliminaryNotice'));
const OhioPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/OhioPreliminaryNotice'));
const NorthCarolinaPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/NorthCarolinaPreliminaryNotice'));
const ColoradoPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/ColoradoPreliminaryNotice'));
const VirginiaPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/VirginiaPreliminaryNotice'));
const WashingtonPreliminaryNotice = lazyWithRetry(() => import('./pages/landing/WashingtonPreliminaryNotice'));

// Industry-specific landing pages
const HVACContractorsNotice = lazyWithRetry(() => import('./pages/landing/HVACContractorsNotice'));
const ElectriciansNotice = lazyWithRetry(() => import('./pages/landing/ElectriciansNotice'));
const PlumbersNotice = lazyWithRetry(() => import('./pages/landing/PlumbersNotice'));
const RoofingContractorsNotice = lazyWithRetry(() => import('./pages/landing/RoofingContractorsNotice'));
const DrywallPaintersNotice = lazyWithRetry(() => import('./pages/landing/DrywallPaintersNotice'));
const BuildingMaterialSuppliersNotice = lazyWithRetry(() => import('./pages/landing/BuildingMaterialSuppliersNotice'));
const EquipmentRentalNotice = lazyWithRetry(() => import('./pages/landing/EquipmentRentalNotice'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-slate-600">Loading...</p>
    </div>
  </div>
);

// noc.allamericannotice.com (and any noc.* host) opens straight on the Florida NOC page.
const IS_NOC_HOST = typeof window !== 'undefined' && window.location.hostname.startsWith('noc.');

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppErrorBoundary>
          <ScrollToTop />
          <ImpersonationBanner />
          <NewsletterCapture />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Pages */}
            <Route path="/" element={IS_NOC_HOST ? <FloridaNoticeOfCommencement /> : <HomePage />} />
            <Route path="/florida-noc" element={<FloridaNoticeOfCommencement />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/testimonials" element={<TestimonialsPage />} />
            <Route path="/learning-center" element={<LearningCenterPage />} />
            <Route path="/learning-center/:stateAbbr" element={<LearningCenterPage />} />
            <Route path="/notice-deadline-calculator" element={<NoticeDeadlineCalculatorPage />} />
            <Route path="/developers" element={<DevelopersPage />} />
            <Route path="/developers/zapier" element={<ZapierIntegrationPage />} />
            <Route path="/design-professionals" element={<DesignProfessionalsPage />} />
            <Route path="/webinar" element={<Navigate to="/" replace />} />

            {/* Public magic-link page for notarized PDF returns */}
            <Route path="/notary-upload/:token" element={<NotaryClientUploadPage />} />
            <Route path="/pay/:token" element={<PayApplicationPage />} />
            <Route path="/w8ben/:token" element={<W8benPage />} />
            <Route path="/sign-agreement/:token" element={<AgreementSignPage />} />
            {/* No-login accept-and-sign page for a proposal */}
            <Route path="/proposal/:token" element={<ProposalAcceptPage />} />
            {/* Magic-link landing page for newly-invited sub-users */}
            <Route path="/set-password" element={<SetPasswordPage />} />
            {/* Password-reset landing page for the "Forgot password?" flow */}
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Procore Embedded Experience — iframe-only, no chrome */}
            <Route path="/embed/procore/notice-request" element={<ProcoreEmbedPage />} />
            <Route path="/auto-login" element={<AutoLoginPage />} />
            
            {/* Blog Pages */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPage />} />
            <Route path="/terms" element={<TermsPage />} />
            
            {/* Service Detail Pages */}
            <Route path="/services/preliminary-notices" element={<PreliminaryNoticesPage />} />
            <Route path="/services/mechanics-liens" element={<MechanicsLiensPage />} />
            <Route path="/services/bond-claims" element={<BondClaimsPage />} />
            <Route path="/services/notice-of-intent" element={<NoticeOfIntentPage />} />
            <Route path="/services/research" element={<ResearchServicesPage />} />
            
            {/* State-Specific Landing Pages (SEO) */}
            <Route path="/florida-preliminary-notice" element={<FloridaPreliminaryNotice />} />
            <Route path="/florida-notice-of-commencement" element={<FloridaNoticeOfCommencement />} />
            <Route path="/texas-preliminary-notice" element={<TexasPreliminaryNotice />} />
            <Route path="/california-preliminary-notice" element={<CaliforniaPreliminaryNotice />} />
            <Route path="/arizona-preliminary-notice" element={<ArizonaPreliminaryNotice />} />
            <Route path="/georgia-preliminary-notice" element={<GeorgiaPreliminaryNotice />} />
            <Route path="/nevada-preliminary-notice" element={<NevadaPreliminaryNotice />} />
            <Route path="/louisiana-preliminary-notice" element={<LouisianaPreliminaryNotice />} />
            <Route path="/new-york-preliminary-notice" element={<NewYorkPreliminaryNotice />} />
            <Route path="/ohio-preliminary-notice" element={<OhioPreliminaryNotice />} />
            <Route path="/north-carolina-preliminary-notice" element={<NorthCarolinaPreliminaryNotice />} />
            <Route path="/colorado-preliminary-notice" element={<ColoradoPreliminaryNotice />} />
            <Route path="/virginia-preliminary-notice" element={<VirginiaPreliminaryNotice />} />
            <Route path="/washington-preliminary-notice" element={<WashingtonPreliminaryNotice />} />
            
            {/* Industry-Specific Landing Pages (SEO) */}
            <Route path="/hvac-contractors-preliminary-notice" element={<HVACContractorsNotice />} />
            <Route path="/electricians-preliminary-notice" element={<ElectriciansNotice />} />
            <Route path="/plumbers-preliminary-notice" element={<PlumbersNotice />} />
            <Route path="/roofing-contractors-preliminary-notice" element={<RoofingContractorsNotice />} />
            <Route path="/drywall-painters-preliminary-notice" element={<DrywallPaintersNotice />} />
            <Route path="/building-material-suppliers-preliminary-notice" element={<BuildingMaterialSuppliersNotice />} />
            <Route path="/equipment-rental-preliminary-notice" element={<EquipmentRentalNotice />} />
            
            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/sms-opt-in" element={<SmsOptInPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            
            {/* Auth Pages */}
            <Route path="/login" element={<AuthPage />} />
            <Route path="/register" element={<AuthPage />} />
            <Route path="/auth" element={<AuthPage />} />
            
            {/* Protected Pages */}
            <Route path="/owner-portal" element={<ProtectedRoute><OwnerPortal /></ProtectedRoute>} />
            <Route path="/rep" element={<ProtectedRoute><RepPortal /></ProtectedRoute>} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <ClientDashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/field-test"
              element={
                <ProtectedRoute>
                  <FieldTestPage />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            {/* Profile settings — signature editor. Role-agnostic; any
                authenticated user can hit /profile/settings from the
                "Set one up in Profile Settings" link in the compose
                email dialog. */}
            <Route 
              path="/profile/settings" 
              element={
                <ProtectedRoute>
                  <ProfileSettingsPage />
                </ProtectedRoute>
              } 
            />
            {/* Back-compat: the old banner link pointed to /client/settings
                which never had a route. Redirect it so nobody sees a 404. */}
            <Route path="/client/settings" element={<Navigate to="/profile/settings" replace />} />
            {/* Back-compat: emails sent before we moved the public-upload
                page onto /api/upload/{id} used /upload/{id}. Forward to
                the backend URL so legacy links keep working. */}
            <Route path="/upload/:orderId" element={<UploadRedirect />} />
            {/* Deep-link aliases so URLs like /admin/developers and
                /admin/developer-console survive refresh and bookmarks.
                The AdminDashboard reads ?view from the query string. */}
            <Route path="/admin/developers" element={<Navigate to="/admin?view=developers" replace />} />
            <Route path="/admin/developer-console" element={<Navigate to="/admin?view=developers" replace />} />
            <Route path="/admin/sales-copilot" element={<Navigate to="/admin?view=sales-copilot" replace />} />
            <Route 
              path="/texas-notices" 
              element={
                <ProtectedRoute requireAdmin>
                  <TexasNoticeManager />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/my-texas-notices" 
              element={
                <ProtectedRoute>
                  <TexasClientManager />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/campaigns" 
              element={
                <ProtectedRoute requireAdmin>
                  <CampaignManager />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/bulk-orders" 
              element={
                <ProtectedRoute requireAdmin>
                  <BulkOrderUtility />
                </ProtectedRoute>
              } 
            />
            {/* Stripe return page for postage deposit checkouts (public) */}
            <Route path="/postage-deposit/return" element={<PostageDepositReturn />} />
            {/* Stripe return page for per-notice invoice payments (public) */}
            <Route path="/per-notice-invoice/return" element={<PerNoticeInvoiceReturn />} />
            {/* Stripe return page for periodic-invoice pay-link payments (public) */}
            <Route path="/invoice/return" element={<InvoicePaymentReturn />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" richColors />
        </AppErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
